import React, { useContext } from "react";

import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureValue } from "@growthbook/growthbook-react";
import { Portal } from "@reach/portal";

import { AppContext } from "~/common";
import { AnchorButton, Button } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import Switch from "~/ui/Switch/Switch";
import { useUXSimplificationFeature } from "~/uxSimplificationFeature";

import { Divider, Label, Left, Right, Row, SettingsPageSubtitle, SettingsPageTitle, SubLabel } from "./shared";

import styles from "./EarlyAccess.module.css";

export default function EarlyAccess() {
	const { user } = useContext(AppContext);
	const { toggleUXSimplification } = useUXSimplificationFeature();
	const inviteLink = useFeatureValue("early-access-slack-link", "");

	return (
		<div className={styles.main}>
			<SettingsPageTitle>Early access</SettingsPageTitle>
			<SettingsPageSubtitle>Manage your preferences</SettingsPageSubtitle>
			<Divider />
			<Row>
				<Left>
					<Label>Try the new Fellow interface</Label>
					<SubLabel>
						Get early access to our new look and feel - only affects your view. You can switch back anytime.
					</SubLabel>
				</Left>
				<Right>
					<Switch size="small" checked={user?.uxSimplificationEnabled} onCheckedChange={toggleUXSimplification} />
				</Right>
			</Row>
			{user?.uxSimplificationEnabled && (
				<div className={styles.feedbackBanner}>
					Your input helps shape the final version. <a href={inviteLink}>Share thoughts in our Slack group.</a>
				</div>
			)}
		</div>
	);
}

function FullPageLoadingSpinner() {
	return (
		<Portal>
			<div className={styles.loadingContainer}>
				<FontAwesomeIcon icon={faSpinnerThird} spin className={styles.loadingSpinner} />
			</div>
		</Portal>
	);
}

export function EarlyAccessModal({ onClose }: { onClose: () => void }) {
	const inviteLink = useFeatureValue("early-access-slack-link", "");
	const [isLoading, setIsLoading] = React.useState(true);

	// We want to show a fake loading state before this modal is shown to the user, hence the setTimeout.
	React.useEffect(() => {
		const timeout = setTimeout(() => {
			setIsLoading(false);
		}, 2000);

		return () => {
			clearTimeout(timeout);
		};
	}, []);

	if (isLoading) {
		return <FullPageLoadingSpinner />;
	}

	return (
		<Modal onClose={onClose}>
			<Modal.Header noBottomBorder>
				<Modal.Title className={styles.modalTitle}>Welcome to the new Fellow (Early Access)</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				<p>
					Thanks for opting into early access. You&apos;re among the first to try Fellow&apos;s new interface.
					We&apos;ve started with updates to meeting notes and recaps—more is coming soon.
				</p>
				<br />
				<p>
					Your feedback will help direct our work and ensure we nail the final roll out! Join our Slack channel to share
					your thoughts and feedback.
				</p>
			</Modal.Body>
			<Modal.Footer className={styles.modalFooter}>
				<Button variant="secondary" onClick={onClose}>
					Start exploring
				</Button>
				{inviteLink !== "" && (
					<AnchorButton variant="primary" href={inviteLink} target="_blank">
						Join Slack channel
					</AnchorButton>
				)}
			</Modal.Footer>
		</Modal>
	);
}
